import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  EventEmitter, HostListener, Inject,
  Input,
  OnChanges, OnDestroy,
  OnInit,
  Output,
  SimpleChanges, ViewChild
} from "@angular/core";
import * as _ from "lodash";
import {PlanningViewSettingsJobsModel} from "../../../models/planning-view-settings.model";
import {
  CdkDrag,
  CdkDragDrop,
  CdkDragEnter,
  CdkDropList,
  moveItemInArray,
  transferArrayItem
} from "@angular/cdk/drag-drop";
import {PlanningPanelModel} from "../../../models/planning-panel.model";
import {CdkVirtualScrollViewport} from "@angular/cdk/scrolling";
import {JobsGroupByEnum} from "../../../types/jobs-group-by.enum";
import {PlanningRouteModel} from "../../../models/planning-route.model";
import {PlanningResourceModel} from "../../../../resources/models/planning-resource.model";
import {PlanningDragAndDropService} from "../../services/planning-drag-and-drop.service";

require('./planning-draft-jobs-panel-editable-list.scss');
@Component({
  standalone: false,
  selector: 'app-planning-draft-jobs-panel-editable-list',
  templateUrl: './planning-draft-jobs-panel-editable-list.html',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class PlanningDraftJobsPanelEditableListComponent implements OnInit, OnChanges, AfterViewInit, OnDestroy {

  @Input() draft?: any;
  @Input() jobs: any;
  @Input() jobsGroupByType: JobsGroupByEnum;
  @Input() routes: PlanningRouteModel[];
  @Input() resources: PlanningResourceModel[];
  @Input() panel: PlanningPanelModel;
  @Input() panels: PlanningPanelModel[];
  @Input() selectedIds: string[];
  @Input() highlightedActivity: any;
  @Input() settings: PlanningViewSettingsJobsModel;
  @Input() itemElementSize: string;
  @Input() expandedGroupedJobs: { [key: string]: boolean } = {};
  @Output() onSelect = new EventEmitter<string[]>();
  @Output() onMoveToLeftovers = new EventEmitter<string[]>();
  @Output() onViewJob = new EventEmitter<{ jobs?: any[], jobIds?: string[], event?: any }>();
  @Output() onToggleObligateTimeWindow = new EventEmitter<{ jobIds: string[], obligateTimeWindow: boolean }>();
  @Output() onContextMenu = new EventEmitter<{ event: any, job: any }>();
  @Output() onReverseJobSplit = new EventEmitter<{ event: MouseEvent, job: any }>();
  @Output() onGroupExpanded = new EventEmitter<{ id: string, expanded: boolean }>();

  @ViewChild(CdkVirtualScrollViewport, {static: false}) private viewport: CdkVirtualScrollViewport

  private resizeObserver: ResizeObserver;
  private scrollEndTimer: any | null = null;

  public connectedPanelIds: string[];
  public selectedKeys: Object;
  public routesByJobMap: Object;
  public isSmallScreen: boolean;

  constructor(
    @Inject('$timeout') private $timeout,
    @Inject(PlanningDragAndDropService) private planningDragAndDropService: PlanningDragAndDropService
    ) {
  }

  ngOnInit() {
    this.isSmallScreen = window.innerWidth < 1366;

    this.resizeObserver = new ResizeObserver(() => {
      this.viewport?.checkViewportSize();
    });
  }

  ngAfterViewInit() {
    if (this.viewport) {
      // Observe the viewport container for size changes
      this.resizeObserver.observe(this.viewport.elementRef.nativeElement);

      // Also observe the content wrapper to catch internal height changes (like expansions)
      const contentWrapper = this.viewport.elementRef.nativeElement.querySelector('.cdk-virtual-scroll-content-wrapper');
      if (contentWrapper) {
        this.resizeObserver.observe(contentWrapper);
      }
    }
  }

  ngOnDestroy() {
    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
    }
    if (this.scrollEndTimer) {
      clearTimeout(this.scrollEndTimer);
      this.scrollEndTimer = null;
    }
  }

  @HostListener('window:resize')
  onResize() {
    this.viewport?.checkViewportSize();
  }

  onScroll(_: Event): void {
    // Workaround for CDK autosize bug: content wrapper occasionally goes blank
    // or out of place after a scroll burst. Self-heal once after scroll stops;
    // doing it per-frame caused layout thrash without a measurable benefit.
    if (this.scrollEndTimer) clearTimeout(this.scrollEndTimer);
    this.scrollEndTimer = setTimeout(() => {
      this.scrollEndTimer = null;
      this._fixViewportIfBroken();
    }, 150);
  }

  private _fixViewportIfBroken(): void {
    if (!this.viewport || !(this.jobs?.length > 0)) return;
    const viewportEl = this.viewport.elementRef.nativeElement;
    const wrapper = viewportEl.querySelector('.cdk-virtual-scroll-content-wrapper');
    if (!wrapper) return;
    const vr = viewportEl.getBoundingClientRect();
    if (vr.height === 0) return; // viewport not visible (hidden tab/panel)
    const wr = wrapper.getBoundingClientRect();
    const isBlank = wr.height === 0;
    const isOutOfPlace = (wr.bottom < vr.top) || (wr.top > vr.bottom);
    if (isBlank || isOutOfPlace) {
      this.viewport.checkViewportSize();
    }
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes.panels || changes.routes || changes.resources) {
      const filteredPanels = _.filter(this.panels, (p) => {
        return p.id !== this.panel.id;
      });
      this.connectedPanelIds = _.concat(_.map(filteredPanels, 'id'), _.map(this.routes, 'id'), _.map(this.resources, 'id'));
    }
    if (changes.selectedIds || changes.jobs) {
      this._initSelectedKeys();
    }
    if (changes.routes) {
      this.routesByJobMap = {};
      if (this.routes?.length) {
        this.routes.forEach((r) => {
          r.jobIds.forEach((id) => {
            this.routesByJobMap[id] = r;
          })
        })
      }
    }

    if (changes.jobs && this.jobs?.length) {
      this.viewport?.checkViewportSize();
    }
  }

  trackById = (_: number, job: { id: string | number }) => job.id;

  onToggleSelectedJob(jobIds: string[], isSelected: boolean) {
    let selectedIds;

    if (isSelected) {
      selectedIds = _.uniq([...this.selectedIds].concat(jobIds));
    } else {
      selectedIds = this.selectedIds.filter(id => !jobIds.includes(id));
    }
    this.onSelect.emit(_.compact(selectedIds));
  }


  drop(event: CdkDragDrop<string[]>) {
    this.planningDragAndDropService.onDropJobs(
      event,
      this.selectedIds
    ).then((jobIds) => {
      if (jobIds) {
        this.onMoveToLeftovers.emit(jobIds);
      }
    });
  }

  private _initSelectedKeys() {
    this.selectedKeys = {};
    this.jobs?.forEach(job => {
      if (job.groupedByType) {
        this.selectedKeys[job.id] = !!job.jobIds.every(item => this.selectedIds.includes(item));
      } else {
        if (this.selectedIds.includes(job.id)) {
          this.selectedKeys[job.id] = true;
        }
      }
    })
  }


}
